import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { cn } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import MobilePageHeader from '../components/MobilePageHeader';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from '../components/ui/dialog';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../components/ui/table';
import { 
    Plus, 
    Users, 
    Edit2,
    Trash2,
    Search,
    Phone,
    Mail,
    DollarSign,
    UserCheck,
    UserX,
    Link2,
    Eye
} from 'lucide-react';
import { PhoneInput, formatPhoneForDisplay } from '../components/ui/phone-input';

const StaffPage = () => {
    const { api, hasPermission, formatCurrency, user } = useAuth();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [staffList, setStaffList] = useState([]);
    const [services, setServices] = useState([]);
    const [positions, setPositions] = useState({});
    const [roles, setRoles] = useState({});
    
    // Check if user is Director or Admin (can see sensitive info)
    const isDirectorOrAdmin = user?.role === 'DIRECTEUR' || user?.role === 'ADMIN';
    // Only Admin can manage staff - Director is READ-ONLY
    const isAdmin = user?.role === 'ADMIN';
    
    // Filters
    const [searchQuery, setSearchQuery] = useState('');
    const [filterService, setFilterService] = useState('');
    const [filterActive, setFilterActive] = useState('true');
    
    // Dialog
    const [showDialog, setShowDialog] = useState(false);
    const [editStaff, setEditStaff] = useState(null);
    const [users, setUsers] = useState([]); // Liste des comptes utilisateurs
    const [formData, setFormData] = useState({
        first_name: '',
        last_name: '',
        full_name: '',
        gender: '',
        date_of_birth: '',
        nationality: 'Gabonaise',
        email: '',
        phone: '',
        address: '',
        id_type: '',
        id_number: '',
        role: 'SERVEUR',
        service: 'Salle',
        position: 'Serveur',
        qualification: '',
        hire_date: '',
        salary_type: 'MONTHLY',
        monthly_salary: 0,
        hourly_rate: 0,
        blood_type: '',
        vaccines_up_to_date: false,
        allergies: '',
        emergency_contact_name: '',
        emergency_contact_phone: '',
        emergency_contact_relation: '',
        uniform_size: '',
        shoe_size: '',
        is_active: true,
        user_id: ''  // Liaison avec compte utilisateur
    });
    
    // Form wizard step
    const [formStep, setFormStep] = useState(1);
    
    // Permissions - Admin only for modifications, Director is read-only
    const canCreate = hasPermission('create_staff') && isAdmin;
    const canEdit = hasPermission('edit_staff') && isAdmin;
    const canDelete = hasPermission('delete_staff') && isAdmin;

    useEffect(() => {
        fetchData();
    }, [filterService, filterActive]);

    const fetchData = async () => {
        try {
            setLoading(true);
            let url = `/staff?is_active=${filterActive}`;
            if (filterService) url += `&service=${filterService}`;
            
            const requests = [
                api().get(url),
                api().get('/services'),
                api().get('/roles')
            ];
            
            // Fetch users list if user has permission
            if (hasPermission('manage_users') || hasPermission('access_admin')) {
                requests.push(api().get('/admin/users').catch(() => ({ data: [] })));
            }
            
            const results = await Promise.all(requests);
            
            setStaffList(results[0].data);
            setServices(results[1].data.services);
            setPositions(results[1].data.positions);
            setRoles(results[2].data.roles || {});
            
            // Set users if available
            if (results[3]) {
                setUsers(results[3].data || []);
            }
        } catch (error) {
            toast.error('Erreur lors du chargement');
        } finally {
            setLoading(false);
        }
    };

    const handleOpenDialog = (staff = null) => {
        if (staff) {
            setEditStaff(staff);
            setFormData({
                first_name: staff.first_name || '',
                last_name: staff.last_name || '',
                full_name: staff.full_name || '',
                gender: staff.gender || '',
                date_of_birth: staff.date_of_birth || '',
                nationality: staff.nationality || 'Gabonaise',
                email: staff.email || '',
                phone: staff.phone || '',
                address: staff.address || '',
                id_type: staff.id_type || '',
                id_number: staff.id_number || '',
                role: staff.role,
                service: staff.service,
                position: staff.position,
                qualification: staff.qualification || '',
                hire_date: staff.hire_date || '',
                salary_type: staff.salary_type,
                monthly_salary: staff.monthly_salary,
                hourly_rate: staff.hourly_rate,
                blood_type: staff.blood_type || '',
                vaccines_up_to_date: staff.vaccines_up_to_date || false,
                allergies: staff.allergies || '',
                emergency_contact_name: staff.emergency_contact_name || '',
                emergency_contact_phone: staff.emergency_contact_phone || '',
                emergency_contact_relation: staff.emergency_contact_relation || '',
                uniform_size: staff.uniform_size || '',
                shoe_size: staff.shoe_size || '',
                is_active: staff.is_active,
                user_id: staff.user_id || ''
            });
        } else {
            setEditStaff(null);
            const today = new Date().toISOString().split('T')[0];
            setFormData({
                first_name: '',
                last_name: '',
                full_name: '',
                gender: '',
                date_of_birth: '',
                nationality: 'Gabonaise',
                email: '',
                phone: '',
                address: '',
                id_type: '',
                id_number: '',
                role: 'SERVEUR',
                service: 'Salle',
                position: 'Serveur',
                qualification: '',
                hire_date: today,
                salary_type: 'MONTHLY',
                monthly_salary: 0,
                hourly_rate: 0,
                blood_type: '',
                vaccines_up_to_date: false,
                allergies: '',
                emergency_contact_name: '',
                emergency_contact_phone: '',
                emergency_contact_relation: '',
                uniform_size: '',
                shoe_size: '',
                is_active: true,
                user_id: ''
            });
        }
        setFormStep(1);
        setShowDialog(true);
    };

    const handleSave = async () => {
        if (!formData.first_name.trim() || !formData.last_name.trim()) {
            toast.error('Le nom et prénom sont requis');
            return;
        }
        
        // Build full_name from first and last name
        const submitData = {
            ...formData,
            full_name: `${formData.first_name} ${formData.last_name}`.trim()
        };
        
        try {
            if (editStaff) {
                await api().put(`/staff/${editStaff.id}`, submitData);
                toast.success('Employe modifie');
            } else {
                await api().post('/staff', submitData);
                toast.success('Employe cree');
            }
            
            setShowDialog(false);
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur');
        }
    };

    const handleDelete = async (staffId, name) => {
        if (!window.confirm(`Desactiver ${name} ?`)) return;
        
        try {
            await api().delete(`/staff/${staffId}`);
            toast.success('Employe desactive');
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur');
        }
    };

    // Toggle employee active/inactive status
    const handleToggleActive = async (staffId, currentStatus, name) => {
        const action = currentStatus ? 'désactiver' : 'activer';
        if (!window.confirm(`Voulez-vous ${action} ${name} ?`)) return;
        
        try {
            await api().patch(`/staff/${staffId}`, {
                is_active: !currentStatus
            });
            toast.success(`${name} ${currentStatus ? 'désactivé' : 'activé'} avec succès`);
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur lors de la modification');
        }
    };

    const filteredStaff = staffList.filter(s => {
        const fullName = s.full_name || s.name || `${s.first_name || ''} ${s.last_name || ''}`.trim() || '';
        const position = s.position || '';
        return fullName.toLowerCase().includes(searchQuery.toLowerCase()) ||
               position.toLowerCase().includes(searchQuery.toLowerCase());
    });

    const getServiceColor = (service) => {
        const colors = {
            'Cuisine': 'bg-orange-100 text-orange-700 border-orange-200',
            'Salle': 'bg-blue-100 text-blue-700 border-blue-200',
            'Bar': 'bg-purple-100 text-purple-700 border-purple-200'
        };
        return colors[service] || 'bg-gray-100 text-gray-700';
    };

    if (loading && !staffList.length) {
        return (
            <div className="space-y-6 animate-pulse">
                <div className="h-8 bg-muted rounded w-48"></div>
                <div className="h-96 bg-muted rounded-xl"></div>
            </div>
        );
    }

    return (
        <div className="space-y-4 md:space-y-6" data-testid="staff-page">
            {/* Mobile Header with back button */}
            <MobilePageHeader 
                title="Personnel" 
                subtitle="Gestion des employés"
                icon={Users}
            />
            
            {/* Desktop Header - hidden on mobile */}
            <div className="hidden md:flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="font-display text-3xl font-bold text-foreground tracking-tight">
                        Personnel
                    </h1>
                    <p className="text-muted-foreground mt-1">
                        Gestion des employes
                    </p>
                </div>
                {canCreate && isAdmin && (
                    <Button onClick={() => handleOpenDialog()} data-testid="add-staff-btn">
                        <Plus className="w-4 h-4 mr-2" />
                        Nouvel employe
                    </Button>
                )}
            </div>
            
            {/* Mobile Add Button */}
            {canCreate && isAdmin && (
                <div className="md:hidden">
                    <Button onClick={() => handleOpenDialog()} className="w-full h-12 touch-target" data-testid="add-staff-btn-mobile">
                        <Plus className="w-5 h-5 mr-2" />
                        Nouvel employé
                    </Button>
                </div>
            )}

            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card>
                    <CardContent className="p-4 flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-accent/10 flex items-center justify-center">
                            <Users className="w-5 h-5 text-accent" />
                        </div>
                        <div>
                            <p className="text-2xl font-bold">{staffList.filter(s => s.is_active).length}</p>
                            <p className="text-xs text-muted-foreground">Actifs</p>
                        </div>
                    </CardContent>
                </Card>
                {services.map(service => (
                    <Card key={service}>
                        <CardContent className="p-4 flex items-center gap-3">
                            <Badge variant="outline" className={getServiceColor(service)}>
                                {service}
                            </Badge>
                            <div>
                                <p className="text-xl font-bold">
                                    {staffList.filter(s => s.service === service && s.is_active).length}
                                </p>
                            </div>
                        </CardContent>
                    </Card>
                ))}
            </div>

            {/* Filters */}
            <Card>
                <CardContent className="p-4">
                    <div className="flex flex-wrap items-center gap-4">
                        <div className="relative flex-1 min-w-[200px]">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                            <Input 
                                placeholder="Rechercher..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="pl-9"
                            />
                        </div>
                        <Select value={filterService || "all"} onValueChange={(v) => setFilterService(v === "all" ? "" : v)}>
                            <SelectTrigger className="w-36">
                                <SelectValue placeholder="Service" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">Tous</SelectItem>
                                {services.map(s => (
                                    <SelectItem key={s} value={s}>{s}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        <Select value={filterActive} onValueChange={setFilterActive}>
                            <SelectTrigger className="w-32">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="true">Actifs</SelectItem>
                                <SelectItem value="false">Inactifs</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                </CardContent>
            </Card>

            {/* Staff Table */}
            <Card>
                <CardContent className="p-0 overflow-x-auto">
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>Nom</TableHead>
                                {isDirectorOrAdmin && <TableHead className="hidden sm:table-cell">Contact</TableHead>}
                                <TableHead>Role</TableHead>
                                <TableHead className="hidden md:table-cell">Service</TableHead>
                                <TableHead className="hidden lg:table-cell">Poste</TableHead>
                                {isDirectorOrAdmin && <TableHead className="hidden md:table-cell text-right">Salaire</TableHead>}
                                <TableHead>Statut</TableHead>
                                <TableHead>Actions</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {filteredStaff.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={isDirectorOrAdmin ? 8 : 6} className="text-center py-8 text-muted-foreground">
                                        Aucun employe trouve
                                    </TableCell>
                                </TableRow>
                            ) : (
                                filteredStaff.map(staff => (
                                    <TableRow key={staff.id}>
                                        <TableCell className="font-medium">{staff.full_name || staff.name || `${staff.first_name || ''} ${staff.last_name || ''}`.trim() || 'Sans nom'}</TableCell>
                                        {isDirectorOrAdmin && (
                                            <TableCell className="hidden sm:table-cell">
                                                <div className="text-xs space-y-1">
                                                    {staff.email && (
                                                        <div className="flex items-center gap-1 text-muted-foreground">
                                                            <Mail className="w-3 h-3" />
                                                            {staff.email}
                                                        </div>
                                                    )}
                                                    {staff.phone && (
                                                        <div className="flex items-center gap-1 text-muted-foreground">
                                                            <Phone className="w-3 h-3" />
                                                            {formatPhoneForDisplay(staff.phone.replace(/^\+\d+/, ''), staff.phone.slice(0, 4)) || staff.phone}
                                                        </div>
                                                    )}
                                                    {staff.user_id && (
                                                        <div className="flex items-center gap-1 text-blue-600">
                                                            <Link2 className="w-3 h-3" />
                                                            <span className="text-xs">Compte lié</span>
                                                        </div>
                                                    )}
                                                </div>
                                            </TableCell>
                                        )}
                                        <TableCell>
                                            <Badge variant="secondary" className="text-xs">
                                                {roles[staff.role]?.label || staff.role}
                                            </Badge>
                                        </TableCell>
                                        <TableCell className="hidden md:table-cell">
                                            <Badge variant="outline" className={getServiceColor(staff.service)}>
                                                {staff.service}
                                            </Badge>
                                        </TableCell>
                                        <TableCell className="hidden lg:table-cell">{staff.position}</TableCell>
                                        {isDirectorOrAdmin && (
                                            <TableCell className="hidden md:table-cell text-right font-mono">
                                                {staff.salary_type === 'MONTHLY' 
                                                    ? formatCurrency(staff.monthly_salary)
                                                    : `${formatCurrency(staff.hourly_rate)}/h`
                                                }
                                            </TableCell>
                                        )}
                                        <TableCell>
                                            {staff.is_active ? (
                                                <Badge className="bg-green-100 text-green-700">
                                                    <UserCheck className="w-3 h-3 mr-1" />
                                                    Actif
                                                </Badge>
                                            ) : (
                                                <Badge variant="outline" className="text-gray-500">
                                                    <UserX className="w-3 h-3 mr-1" />
                                                    Inactif
                                                </Badge>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            <div className="flex gap-1">
                                                {/* Bouton Voir - toujours visible */}
                                                <Button 
                                                    variant="ghost" 
                                                    size="icon" 
                                                    className="h-8 w-8"
                                                    onClick={() => navigate(`/staff/${staff.id}`)}
                                                    title="Voir la fiche"
                                                    data-testid={`view-staff-${staff.id}`}
                                                >
                                                    <Eye className="w-4 h-4" />
                                                </Button>
                                                {isAdmin && canEdit && (
                                                    <Button 
                                                        variant="ghost" 
                                                        size="icon" 
                                                        className="h-8 w-8"
                                                        onClick={() => handleOpenDialog(staff)}
                                                        title="Modifier"
                                                        data-testid={`edit-staff-${staff.id}`}
                                                    >
                                                        <Edit2 className="w-4 h-4" />
                                                    </Button>
                                                )}
                                                {/* Toggle Active/Inactive button */}
                                                {isAdmin && (
                                                    <Button 
                                                        variant="ghost" 
                                                        size="icon" 
                                                        className={cn(
                                                            "h-8 w-8",
                                                            staff.is_active ? "text-orange-500 hover:text-orange-600" : "text-green-500 hover:text-green-600"
                                                        )}
                                                        onClick={() => handleToggleActive(staff.id, staff.is_active, staff.full_name || staff.name || 'cet employé')}
                                                        title={staff.is_active ? "Désactiver" : "Activer"}
                                                        data-testid={`toggle-active-${staff.id}`}
                                                    >
                                                        {staff.is_active ? <UserX className="w-4 h-4" /> : <UserCheck className="w-4 h-4" />}
                                                    </Button>
                                                )}
                                                {isAdmin && canDelete && staff.is_active && (
                                                    <Button 
                                                        variant="ghost" 
                                                        size="icon" 
                                                        className="h-8 w-8 text-destructive"
                                                        onClick={() => handleDelete(staff.id, staff.full_name || staff.name || 'cet employé')}
                                                        title="Supprimer"
                                                        data-testid={`delete-staff-${staff.id}`}
                                                    >
                                                        <Trash2 className="w-4 h-4" />
                                                    </Button>
                                                )}
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </CardContent>
            </Card>

            {/* Add/Edit Dialog - Multi-step Form */}
            <Dialog open={showDialog} onOpenChange={setShowDialog}>
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>
                            {editStaff ? 'Modifier l\'employé' : 'Nouvel employé'}
                        </DialogTitle>
                        {/* Step indicator */}
                        <div className="flex items-center justify-center gap-2 mt-4">
                            {[1, 2, 3, 4].map((step) => (
                                <div key={step} className="flex items-center">
                                    <div 
                                        className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium cursor-pointer
                                            ${formStep === step ? 'bg-primary text-primary-foreground' : 
                                              formStep > step ? 'bg-green-500 text-white' : 'bg-muted text-muted-foreground'}`}
                                        onClick={() => setFormStep(step)}
                                    >
                                        {step}
                                    </div>
                                    {step < 4 && <div className={`w-12 h-1 ${formStep > step ? 'bg-green-500' : 'bg-muted'}`}></div>}
                                </div>
                            ))}
                        </div>
                        <p className="text-center text-sm text-muted-foreground mt-2">
                            {formStep === 1 && "Identité"}
                            {formStep === 2 && "Contact & Documents"}
                            {formStep === 3 && "Emploi & Salaire"}
                            {formStep === 4 && "Santé & Urgence"}
                        </p>
                    </DialogHeader>
                    
                    <div className="space-y-4 py-4">
                        {/* Step 1: Identity */}
                        {formStep === 1 && (
                            <>
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <Label>Prénom *</Label>
                                        <Input 
                                            value={formData.first_name}
                                            onChange={(e) => setFormData({...formData, first_name: e.target.value})}
                                            placeholder="Prénom"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label>Nom *</Label>
                                        <Input 
                                            value={formData.last_name}
                                            onChange={(e) => setFormData({...formData, last_name: e.target.value})}
                                            placeholder="Nom de famille"
                                        />
                                    </div>
                                </div>
                                
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <Label>Genre</Label>
                                        <Select value={formData.gender} onValueChange={(v) => setFormData({...formData, gender: v})}>
                                            <SelectTrigger><SelectValue placeholder="Sélectionner" /></SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="Homme">Homme</SelectItem>
                                                <SelectItem value="Femme">Femme</SelectItem>
                                                <SelectItem value="Autre">Autre</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div className="space-y-2">
                                        <Label>Date de naissance</Label>
                                        <Input 
                                            type="date"
                                            value={formData.date_of_birth}
                                            onChange={(e) => setFormData({...formData, date_of_birth: e.target.value})}
                                        />
                                    </div>
                                </div>
                                
                                <div className="space-y-2">
                                    <Label>Nationalité</Label>
                                    <Input 
                                        value={formData.nationality}
                                        onChange={(e) => setFormData({...formData, nationality: e.target.value})}
                                        placeholder="Nationalité"
                                    />
                                </div>
                                
                                {/* Liaison compte utilisateur */}
                                {users.length > 0 && (
                                    <div className="space-y-2 p-3 bg-muted/50 rounded-lg">
                                        <Label className="flex items-center gap-2">
                                            <Link2 className="w-4 h-4" />
                                            Lier à un compte utilisateur
                                        </Label>
                                        <Select 
                                            value={formData.user_id || 'none'} 
                                            onValueChange={(v) => {
                                                const selectedUser = users.find(u => u.id === v);
                                                if (selectedUser && v !== 'none') {
                                                    setFormData({
                                                        ...formData, 
                                                        user_id: v,
                                                        first_name: formData.first_name || selectedUser.name.split(' ')[0],
                                                        last_name: formData.last_name || selectedUser.name.split(' ').slice(1).join(' '),
                                                        email: formData.email || selectedUser.email
                                                    });
                                                } else {
                                                    setFormData({...formData, user_id: ''});
                                                }
                                            }}
                                        >
                                            <SelectTrigger>
                                                <SelectValue placeholder="Aucun compte lié" />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="none">Aucun compte lié</SelectItem>
                                                {users.filter(u => u.is_active).map(user => (
                                                    <SelectItem key={user.id} value={user.id}>
                                                        {user.name} ({user.role}) - {user.email}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                        <p className="text-xs text-muted-foreground">
                                            Permet à cet utilisateur de pointer avec son compte
                                        </p>
                                    </div>
                                )}
                            </>
                        )}
                        
                        {/* Step 2: Contact & Documents */}
                        {formStep === 2 && (
                            <>
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <Label className="flex items-center gap-2"><Mail className="w-4 h-4" /> Email</Label>
                                        <Input 
                                            type="email"
                                            value={formData.email}
                                            onChange={(e) => setFormData({...formData, email: e.target.value})}
                                            placeholder="email@exemple.com"
                                        />
                                    </div>
                                    <PhoneInput
                                        label="Téléphone"
                                        value={formData.phone}
                                        onChange={(phone) => setFormData({...formData, phone})}
                                    />
                                </div>
                                
                                <div className="space-y-2">
                                    <Label>Adresse</Label>
                                    <Input 
                                        value={formData.address}
                                        onChange={(e) => setFormData({...formData, address: e.target.value})}
                                        placeholder="Adresse complète"
                                    />
                                </div>
                                
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <Label>Type de pièce d'identité</Label>
                                        <Select value={formData.id_type} onValueChange={(v) => setFormData({...formData, id_type: v})}>
                                            <SelectTrigger><SelectValue placeholder="Sélectionner" /></SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="CNI">CNI</SelectItem>
                                                <SelectItem value="Passeport">Passeport</SelectItem>
                                                <SelectItem value="Carte de séjour">Carte de séjour</SelectItem>
                                                <SelectItem value="Permis de conduire">Permis de conduire</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div className="space-y-2">
                                        <Label>Numéro de pièce</Label>
                                        <Input 
                                            value={formData.id_number}
                                            onChange={(e) => setFormData({...formData, id_number: e.target.value})}
                                            placeholder="Numéro"
                                        />
                                    </div>
                                </div>
                            </>
                        )}
                        
                        {/* Step 3: Employment & Salary */}
                        {formStep === 3 && (
                            <>
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <Label>Service</Label>
                                        <Select 
                                            value={formData.service} 
                                            onValueChange={(v) => setFormData({...formData, service: v, position: positions[v]?.[0] || ''})}
                                        >
                                            <SelectTrigger><SelectValue /></SelectTrigger>
                                            <SelectContent>
                                                {services.map(s => (
                                                    <SelectItem key={s} value={s}>{s}</SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div className="space-y-2">
                                        <Label>Poste</Label>
                                        <Select 
                                            value={formData.position} 
                                            onValueChange={(v) => setFormData({...formData, position: v})}
                                        >
                                            <SelectTrigger><SelectValue /></SelectTrigger>
                                            <SelectContent>
                                                {(positions[formData.service] || []).map(p => (
                                                    <SelectItem key={p} value={p}>{p}</SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                </div>
                                
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <Label>Rôle système</Label>
                                        <Select 
                                            value={formData.role} 
                                            onValueChange={(v) => setFormData({...formData, role: v})}
                                        >
                                            <SelectTrigger><SelectValue /></SelectTrigger>
                                            <SelectContent>
                                                {Object.entries(roles).map(([key, role]) => (
                                                    <SelectItem key={key} value={key}>
                                                        {role.label}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div className="space-y-2">
                                        <Label>Date d'embauche</Label>
                                        <Input 
                                            type="date"
                                            value={formData.hire_date}
                                            onChange={(e) => setFormData({...formData, hire_date: e.target.value})}
                                        />
                                    </div>
                                </div>
                                
                                <div className="space-y-2">
                                    <Label>Qualification / Diplômes</Label>
                                    <Input 
                                        value={formData.qualification}
                                        onChange={(e) => setFormData({...formData, qualification: e.target.value})}
                                        placeholder="CAP Cuisine, BTS Hôtellerie..."
                                    />
                                </div>
                                
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <Label>Type de salaire</Label>
                                        <Select 
                                            value={formData.salary_type} 
                                            onValueChange={(v) => setFormData({...formData, salary_type: v})}
                                        >
                                            <SelectTrigger><SelectValue /></SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="MONTHLY">Mensuel</SelectItem>
                                                <SelectItem value="HOURLY">Horaire</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    {formData.salary_type === 'MONTHLY' ? (
                                        <div className="space-y-2">
                                            <Label>Salaire mensuel (CFA)</Label>
                                            <Input 
                                                type="number"
                                                value={formData.monthly_salary}
                                                onChange={(e) => setFormData({...formData, monthly_salary: Number(e.target.value)})}
                                            />
                                        </div>
                                    ) : (
                                        <div className="space-y-2">
                                            <Label>Taux horaire (CFA/h)</Label>
                                            <Input 
                                                type="number"
                                                value={formData.hourly_rate}
                                                onChange={(e) => setFormData({...formData, hourly_rate: Number(e.target.value)})}
                                            />
                                        </div>
                                    )}
                                </div>
                                
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <Label>Taille uniforme</Label>
                                        <Select value={formData.uniform_size} onValueChange={(v) => setFormData({...formData, uniform_size: v})}>
                                            <SelectTrigger><SelectValue placeholder="Taille" /></SelectTrigger>
                                            <SelectContent>
                                                {['XS', 'S', 'M', 'L', 'XL', 'XXL', 'XXXL'].map(s => (
                                                    <SelectItem key={s} value={s}>{s}</SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div className="space-y-2">
                                        <Label>Pointure</Label>
                                        <Input 
                                            value={formData.shoe_size}
                                            onChange={(e) => setFormData({...formData, shoe_size: e.target.value})}
                                            placeholder="Ex: 42"
                                        />
                                    </div>
                                </div>
                            </>
                        )}
                        
                        {/* Step 4: Health & Emergency */}
                        {formStep === 4 && (
                            <>
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <Label>Groupe sanguin</Label>
                                        <Select value={formData.blood_type} onValueChange={(v) => setFormData({...formData, blood_type: v})}>
                                            <SelectTrigger><SelectValue placeholder="Sélectionner" /></SelectTrigger>
                                            <SelectContent>
                                                {['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-'].map(bt => (
                                                    <SelectItem key={bt} value={bt}>{bt}</SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div className="space-y-2">
                                        <Label>Vaccins à jour</Label>
                                        <Select 
                                            value={formData.vaccines_up_to_date ? 'true' : 'false'} 
                                            onValueChange={(v) => setFormData({...formData, vaccines_up_to_date: v === 'true'})}
                                        >
                                            <SelectTrigger><SelectValue /></SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="true">Oui</SelectItem>
                                                <SelectItem value="false">Non</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>
                                </div>
                                
                                <div className="space-y-2">
                                    <Label>Allergies connues</Label>
                                    <Input 
                                        value={formData.allergies}
                                        onChange={(e) => setFormData({...formData, allergies: e.target.value})}
                                        placeholder="Liste des allergies (alimentaires, médicamenteuses...)"
                                    />
                                </div>
                                
                                <div className="p-4 bg-red-50 dark:bg-red-950/20 rounded-lg border border-red-200 dark:border-red-900">
                                    <h4 className="font-medium text-red-700 dark:text-red-400 mb-3 flex items-center gap-2">
                                        <Phone className="w-4 h-4" />
                                        Contact d'urgence
                                    </h4>
                                    <div className="grid grid-cols-2 gap-4">
                                        <div className="space-y-2">
                                            <Label>Nom du contact</Label>
                                            <Input 
                                                value={formData.emergency_contact_name}
                                                onChange={(e) => setFormData({...formData, emergency_contact_name: e.target.value})}
                                                placeholder="Nom complet"
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <Label>Relation</Label>
                                            <Select value={formData.emergency_contact_relation} onValueChange={(v) => setFormData({...formData, emergency_contact_relation: v})}>
                                                <SelectTrigger><SelectValue placeholder="Lien" /></SelectTrigger>
                                                <SelectContent>
                                                    {['Père', 'Mère', 'Conjoint(e)', 'Frère', 'Soeur', 'Enfant', 'Ami(e)', 'Autre'].map(r => (
                                                        <SelectItem key={r} value={r}>{r}</SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        </div>
                                    </div>
                                    <div className="mt-3">
                                        <PhoneInput
                                            label="Téléphone d'urgence"
                                            value={formData.emergency_contact_phone}
                                            onChange={(phone) => setFormData({...formData, emergency_contact_phone: phone})}
                                        />
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                    
                    <DialogFooter className="flex justify-between">
                        <div>
                            {formStep > 1 && (
                                <Button variant="outline" onClick={() => setFormStep(formStep - 1)}>
                                    Précédent
                                </Button>
                            )}
                        </div>
                        <div className="flex gap-2">
                            <Button variant="outline" onClick={() => setShowDialog(false)}>
                                Annuler
                            </Button>
                            {formStep < 4 ? (
                                <Button onClick={() => setFormStep(formStep + 1)}>
                                    Suivant
                                </Button>
                            ) : (
                                <Button onClick={handleSave}>
                                    {editStaff ? 'Modifier' : 'Créer'}
                                </Button>
                            )}
                        </div>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default StaffPage;
